import { graphqlClient } from '@/lib/graphql-client';
import { buildPagingLiteral, buildWhereLiteral } from '@/lib/graphql-query-builder';
import {
  dateOnlyOf,
  dateOnlyRangeClause,
  orgCalendarDate,
  toDateOnlyValue,
} from '@/lib/utils/custom-date/custom-date';
import { holidayDaysOf } from '@/modules/leave/hr-leave-manager/utils/leave-days';
import { LeaveDayChange, leaveDayChangesOf, OpenLeave, TermWorkDays } from './leave-day-changes';

const PAGE_SIZE = 500;

/**
 * The employee's open leaves (Pending and Approved, not over before `today`) whose days a save of the HR terms
 * from `before` to `after` changes (`leaveDayChangesOf`), with the organization's holidays of their days.
 */
export const getLeaveDayChanges = async ({
  userId,
  before,
  after,
}: {
  userId: string;
  before: readonly TermWorkDays[];
  after: readonly TermWorkDays[];
}): Promise<LeaveDayChange[]> => {
  const today = orgCalendarDate(new Date().toISOString());
  const leaves = (
    await graphqlClient.query<{ LeaveRequests: { items: OpenLeave[] } }>({
      query: `
        query {
          LeaveRequests: getLeaveRequests(
            where: ${buildWhereLiteral({
              UserId: userId,
              Status: { $in: ['Pending', 'Approved'] },
              EndDate: { $gte: toDateOnlyValue(today) },
            })}
            order: []
            paging: ${buildPagingLiteral(1, PAGE_SIZE)}
          ) {
            items { ItemId LeaveCategory LeaveType Status StartDate EndDate }
          }
        }
      `,
    })
  ).LeaveRequests.items;
  if (leaves.length === 0) return [];
  const first = leaves.map((leave) => dateOnlyOf(leave.StartDate)).sort()[0];
  const last = leaves
    .map((leave) => dateOnlyOf(leave.EndDate))
    .sort()
    .reverse()[0];
  const range = dateOnlyRangeClause(first, last);
  const holidays = range
    ? (
        await graphqlClient.query<{ Holidays: { items: { StartDate: string }[] } }>({
          query: `
            query {
              Holidays: getHolidays(
                where: ${buildWhereLiteral({ StartDate: range })}
                order: []
                paging: ${buildPagingLiteral(1, PAGE_SIZE)}
              ) {
                items { StartDate }
              }
            }
          `,
        })
      ).Holidays.items
    : [];
  return leaveDayChangesOf({ leaves, before, after, holidays: holidayDaysOf(holidays), today });
};
